import {
  AccountLimitedResponse,
  ApiError,
  OrganizationsService,
  ParentLimitedResponse,
  StudentLimitedResponse,
  TaskResponse,
  TasksService,
  TaskStatus,
  TaskUpdate,
  TutorPublicResponse,
} from "client/openapi";
import type { TaskDue } from "client/openapi/models/TaskDue";
import {
  AutomaticChip,
  automationOfTask,
  NextUp,
  useTaskAutomations,
} from "components/Automations/TaskAutomations";
import type { ApprovalItem } from "components/Automations/types";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import {
  recipientChanges,
  recipientPerson,
  taskPeople,
} from "components/Dashboard/ManageTasksDialog/PeoplePicker";
import TaskFields, {
  TaskDraft,
} from "components/Dashboard/ManageTasksDialog/TaskFields";
import { announceTasksChanged } from "components/Dashboard/ManageTasksDialog/tasksChanged";
import "components/Dashboard/ManageTasksDialog/create.css";
import { Dialog, DialogContent } from "components/Dialog";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import Notifications from "util/notifications";

function draftOf(task: TaskResponse): TaskDraft {
  return {
    name: task.name,
    content: task.content,
    due_date: task.due_date,
    assigned: task.assigned_users.flatMap(recipientPerson),
    tagged: task.tagged_users.flatMap(recipientPerson),
  };
}

/**
 * The tasks behind a Task due chip or a task row, in a window over the
 * dashboard: one task opens on its details; several are listed, each to
 * open or mark done. A task's details show as the task form reads them,
 * with Edit (the same fields) and Mark done.
 */
export default function TaskViewDialog({
  tasks,
  orgId,
  loadedTasks,
  onClose,
  onChanged,
}: {
  tasks: Pick<TaskDue, "id" | "name" | "due_date" | "overdue">[];
  orgId: number;
  /**
   * The tasks in full when the page has them (a person's Tasks section);
   * otherwise the organization's are loaded, which only an admin may.
   */
  loadedTasks?: TaskResponse[];
  onClose: () => void;
  /** A task was edited or marked done. */
  onChanged: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState<Map<number, TaskResponse>>();
  const [organization, setOrganization] = useState<{
    admins?: AccountLimitedResponse[];
    tutors?: TutorPublicResponse[];
    parents?: ParentLimitedResponse[];
    students?: StudentLimitedResponse[];
  }>({});
  const [failed, setFailed] = useState(false);
  const [openId, setOpenId] = useState<number | undefined>(
    tasks.length === 1 ? tasks[0].id : undefined
  );
  const [draft, setDraft] = useState<TaskDraft>();
  const [saving, setSaving] = useState(false);
  // Automations hear of a status change; what pops up for this person.
  const automations = useTaskAutomations();
  const [nextUp, setNextUp] = useState<ApprovalItem[]>([]);
  // The page behind reloads once Next up closes: a reload that drops the
  // task's row would take this window away, and Next up with it.
  const afterNextUp = useRef<(() => void) | null>(null);
  const shown = useRef(true);
  const closeNextUp = () => {
    setNextUp([]);
    afterNextUp.current?.();
    afterNextUp.current = null;
  };
  // Gone first (the window closed while it checked): the page still reloads.
  useEffect(
    () => () => {
      shown.current = false;
      afterNextUp.current?.();
    },
    []
  );

  useEffect(() => {
    Promise.all([
      loadedTasks ?? TasksService.getOrgTasks({ orgId }),
      OrganizationsService.getOrganization({ orgId }),
    ])
      .then(([orgTasks, org]) => {
        setLoaded(new Map(orgTasks.map((task) => [task.id, task])));
        setOrganization(org as typeof organization);
      })
      .catch(() => setFailed(true));
  }, [orgId, loadedTasks]);

  function save(task: TaskResponse, update: TaskUpdate) {
    setSaving(true);
    TasksService.updateTask({ taskId: task.id, requestBody: update })
      .then((updated) => {
        setLoaded((current) => new Map(current).set(updated.id, updated));
        setDraft(undefined);
        Notifications.success(
          update.status === TaskStatus.COMPLETE
            ? "Task marked done."
            : "Task updated."
        );
        announceTasksChanged();
        if (!update.status || update.status === task.status) {
          onChanged();
          return;
        }
        automations
          .changedInTadpole(task, updated)
          .catch((): ApprovalItem[] => [])
          .then((items) => {
            if (items.length && shown.current) {
              afterNextUp.current = onChanged;
              setNextUp(items);
            } else onChanged();
          });
      })
      .catch((error: ApiError) =>
        Notifications.error(error.body?.detail || "Could not save the task.")
      )
      .finally(() => setSaving(false));
  }

  function saveDraft(task: TaskResponse, edited: TaskDraft) {
    const before = draftOf(task);
    const assigned = recipientChanges(before.assigned, edited.assigned);
    const tagged = recipientChanges(before.tagged, edited.tagged);
    if (!edited.name.trim() || edited.assigned.length === 0) {
      Notifications.error(
        "Add a task name and at least one person to assign it to."
      );
      return;
    }
    // Only what changed is sent: a field present is a field set.
    const changed = <T,>(after: T, now: T) =>
      after !== now ? after : undefined;
    const some = <T,>(list: T[]) => (list.length ? list : undefined);
    save(task, {
      name: changed(edited.name, task.name),
      content: changed(edited.content, task.content),
      due_date: changed(edited.due_date, task.due_date),
      add_assigned_users: some(assigned.add),
      remove_assigned_users: some(assigned.remove),
      add_tagged_users: some(tagged.add),
      remove_tagged_users: some(tagged.remove),
    });
  }

  const open = openId !== undefined ? loaded?.get(openId) : undefined;
  const done = (task: TaskResponse) => task.status === TaskStatus.COMPLETE;

  let body;
  if (failed) {
    body = <p className="text-sm text-gray-700">Could not load the task.</p>;
  } else if (!loaded) {
    body = <p className="text-sm text-gray-500">Loading…</p>;
  } else if (open) {
    body = (
      <>
        <TaskFields
          idPrefix={`task-view-${open.id}`}
          task={draft ?? draftOf(open)}
          onChange={setDraft}
          people={taskPeople(organization)}
          suggestedAssign={[]}
          suggestedAbout={[]}
          dateContainer={() => dialogRef.current || document.body}
          readOnly={!draft}
        />
        <div className="task-form__actions">
          {tasks.length > 1 && !draft && (
            <Button
              size={ButtonSize.SMALL}
              fill={ButtonFill.HOLLOW}
              color={ButtonColor.GREEN}
              onClick={() => setOpenId(undefined)}
            >
              All tasks
            </Button>
          )}
          {draft ? (
            <>
              <Button
                size={ButtonSize.SMALL}
                fill={ButtonFill.HOLLOW}
                color={ButtonColor.GREEN}
                onClick={() => setDraft(undefined)}
              >
                Cancel
              </Button>
              <Button
                size={ButtonSize.SMALL}
                color={ButtonColor.GREEN}
                disabled={saving}
                onClick={() => saveDraft(open, draft)}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                size={ButtonSize.SMALL}
                fill={ButtonFill.HOLLOW}
                color={ButtonColor.GREEN}
                onClick={() => setDraft(draftOf(open))}
              >
                Edit
              </Button>
              <Button
                size={ButtonSize.SMALL}
                color={ButtonColor.GREEN}
                disabled={saving || done(open)}
                onClick={() => save(open, { status: TaskStatus.COMPLETE })}
              >
                {done(open) ? "Done" : "Mark done"}
              </Button>
            </>
          )}
        </div>
      </>
    );
  } else {
    body = (
      <ul className="people-tasks">
        {tasks.map((due) => {
          const task = loaded.get(due.id);
          return (
            <li key={due.id} className="people-tasks__item">
              <div className="min-w-0">
                <p className="people-tasks__name">{due.name}</p>
                <p
                  className={`people-tasks__due${
                    due.overdue ? " people-tasks__due--overdue" : ""
                  }`}
                >
                  {due.overdue ? "Overdue · " : "Due "}
                  {moment(due.due_date).format("ddd, MMM D")}
                </p>
              </div>
              <div className="flex flex-none gap-2">
                <Button
                  size={ButtonSize.SMALL}
                  fill={ButtonFill.HOLLOW}
                  color={ButtonColor.GREEN}
                  disabled={!task}
                  onClick={() => setOpenId(due.id)}
                >
                  Open
                </Button>
                <Button
                  size={ButtonSize.SMALL}
                  color={ButtonColor.GREEN}
                  disabled={!task || saving || done(task)}
                  onClick={() =>
                    task && save(task, { status: TaskStatus.COMPLETE })
                  }
                >
                  {task && done(task) ? "Done" : "Mark done"}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent ref={dialogRef} className="dialog-content task-form">
        <h2 className="task-form__title">
          {open ? (draft ? "Edit task" : "Task") : `Tasks (${tasks.length})`}
          {open && automationOfTask(open) && (
            <span className="ml-2 align-middle">
              <AutomaticChip automation={automationOfTask(open)!} />
            </span>
          )}
        </h2>
        {body}
        <NextUp
          key={nextUp[0]?.id}
          orgId={orgId}
          items={nextUp}
          onClose={closeNextUp}
        />
      </DialogContent>
    </Dialog>
  );
}
